import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TickerAnimation from "../../_utils/TickerAnimation";
import CommonBankLogo from "../../_utils/CommonBankLogo";
import { formatAccountNumber } from "../../_utils/FormatAccountNumber";
import CommonAccountModal from "../../_utils/TransactionCommonComponents/SourceDestinationAccountDetail/CommonAccountModal";
import ICONS from "../../../assets/Icons";
import IconTearDropButton from "../../_utils/CommonButtons/IconTearDropButton";
import SVGComponent from "../../_utils/SVGComponent";
import PrimaryWithIconButton from "../../_utils/CommonButtons/PrimaryWithIconButton";
import TEXT from "../../../utils/globalText";
import LargePopupRedirect from "../../_utils/CommonPopups/CommonLargePopupRedirect";
import ReimbursementAccountViewAll from "../../../containers/Accounts/ReimbursementAccount/AccountViewAll";
import accountNameStandardised from "../../../utils/accountNamestandardised";
import CommonToast from "../../_utils/CommonToast";

// Suggestions of props for component while using the coFmponents -------------------------------------
/**
 * @param {String} props.type - passs type like "source" or "destination"
 * @param {String} props.flow - If type is "destination" or "internal", then pass "beneficiary" or "internal"
 * @param {String} props.page - If you want this component on confirm page then, pass "confirm"
 * @param {Boolean} props.modalOpen - Is modal open or not
 * @param {Object} props.setModalOpen - Setter of modal state ( true or false )
 * @param {Object} props.txnPayLoad - txnPayload array
 * @param {Object} props.setTxnPayLoad - Setter of txnPayload
 * @param {Boolean} props.shouldShowAssociatedAccounts -  If you are using type = "destination", then pass state of showing associted accounts found
 * @param {Function} props.handleOnAccountSelection - If you are using type = "destination", then pass handle function of Associate account found
 * @returns {JSX.Element}
 */

const DestinationFieldForce = ({
  type,
  flow,
  page,
  modalOpen,
  setModalOpen,
  txnPayLoad,
  setTxnPayLoad,
  shouldShowAssociatedAccounts,
  handleOnAccountSelection,
  setActiveType,
}) => {
  const navigate = useNavigate();
  const services = useSelector((state) => state.refreshToken.services);
  const [openBeneAssociate, setOpenBeneAssociate] = useState(false);
  const [remPan, setRemPan] = useState("");
  const [remAccount, setRemAccount] = useState("");
  const [remIFSC, setRemIFSC] = useState("");
  const requestActiveRef = useRef(false);
  const toastIdRef = useRef(null);
  const completedRef = useRef(false);
  const hasSeenLoadingRef = useRef(false);

  //states, variables and logics for Source account (starts) ---------------------------------

  const {
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshError,
    createTransactionBankRefreshData,
  } = useSelector((state) => state.ownerAllVerification);

  const cleanupAfterRefresh = () => {
    toastIdRef.current = null;
    requestActiveRef.current = false;
    completedRef.current = false;
    hasSeenLoadingRef.current = false;
  };

  useEffect(() => {
    // Track when loading starts to ensure we only process errors from actual API calls
    if (createTransactionBankRefreshLoading && requestActiveRef.current) {
      hasSeenLoadingRef.current = true;
      return;
    }

    // Ignore anything after completion or if no request was made
    if (!requestActiveRef.current || completedRef.current) return;

    // Only process success/error if we've seen loading start (to avoid stale errors)
    if (!hasSeenLoadingRef.current) return;

    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshData?.status
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);
      CommonToast({
        text: "Balance refreshed successfully.",
        icon: ICONS?.SELECTED_WITH_WHITE_FILLED,
        variant: "var(--success)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });

      cleanupAfterRefresh();
      return;
    }

    // Only show error if a request was actually made and we've seen loading
    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshError &&
      requestActiveRef.current &&
      hasSeenLoadingRef.current
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);
      CommonToast({
        text: createTransactionBankRefreshError,
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });

      cleanupAfterRefresh();
    }
  }, [
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshData,
    createTransactionBankRefreshError,
  ]);

  //states, variables and logics  for Source account (Ends) ---------------------------------

  const handleOpenModal = () => {
    if (page !== "confirm") {
      setModalOpen(true);
    }
  };

  const handleAssociateOpenModal = () => {
    setRemPan(txnPayLoad?.pan);
    setOpenBeneAssociate(true);
  };

  const registerNewAccount = () => {
    navigate("/reimbursement_account_dashboard/create_reimbursement_account");
  };

  const hasBUAccountService = services?.includes("BUAccount");

  return (
    <>
      {modalOpen && (
        <div className="absolute top-top-base left-left-base w-full-width min-h-full-height flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <LargePopupRedirect
            closeiconhandleFunction={() => {
              setModalOpen(false);
            }}
            isfullViewrequired={true}
            togglepan={() => {
              setActiveType("pan");
              setRemAccount("");
              setRemIFSC("");
            }}
            toggleBank={() => {
              setActiveType("bank");
              setRemPan("");
            }}
            fullviewbtnText="Register a new account"
            redirectHeader="Register a new account"
            fullviewhandleFunction={() => {
              navigate(
                "/reimbursement_account_dashboard/create_reimbursement_account",
              );
            }}
            redirectIcon={
              "https://q2pay.in/new_assets/common/settings/add_button_white.svg"
            }
            fullviewicon={ICONS?.REDIRECT}
            isbottomSectionneed={false}
            headerTitle={"Reimbursement account"}
            headersubtext={
              "Only active (pending & approved) accounts are enabled for selection."
            }
          >
            <ReimbursementAccountViewAll
              type={"ReimbursementAcc"}
              flow="create"
              setModalOpen={setModalOpen}
              setTxnPayLoad={setTxnPayLoad}
              onAccountSelect={handleOnAccountSelection}
            />
          </LargePopupRedirect>
        </div>
      )}
      {openBeneAssociate && (
        <div className="fixed top-top-base left-left-base w-screen h-screen z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <LargePopupRedirect
            headerTitle="Associated accounts"
            headersubtext="The accounts listed below are linked to the selected reimbursement account."
            closeiconenabledBgColor="var(--primary-nav)"
            closeiconisLoading={false}
            closeiconhandleFunction={() => {
              setOpenBeneAssociate(false);
              setRemPan("");
              setRemAccount("");
              setRemIFSC("");
              setActiveType("pan");
            }}
            closeicon={
              "https://q2pay.in/new_assets/common/create_flow/close.svg"
            }
            closeiconhoverOption="white"
            closeiconbtnType={"button"}
            closeiconisDisabled={false}
            closeicondisabledBgColor={""}
            closeiconbuttonRef={null}
            closeicontitle={"Close"}
            btnType={"button"}
            confirmbtnrequired={true}
            enabledBgColor={"var(--negative-act)"}
            hoverOption={"dark"}
            outLineColor={""}
            enabledTextColor={"var(--text-primary)"}
            fullWidthRequired={false}
            isDisabled={false}
            disabledTextColor={""}
            disabledBgColor={""}
            ispanbankRequired={true}
            togglepan={() => {
              setActiveType("pan");
              setRemPan(txnPayLoad?.pan);
              setRemAccount("");
              setRemIFSC("");
            }}
            toggleBank={() => {
              setActiveType("bank");
              setRemPan("");
              setRemAccount(txnPayLoad?.bankAccountNo);
              setRemIFSC(txnPayLoad?.bankAccountIFSC);
            }}
          >
            <ReimbursementAccountViewAll
              type={"ReimbursementAcc"}
              flow={"create"}
              popupType={"associated"}
              remPan={remPan}
              remAccount={remAccount}
              remIFSC={remIFSC}
              setModalOpen={setModalOpen}
              setOpenBeneAssociate={setOpenBeneAssociate}
              onAccountSelect={handleOnAccountSelection}
              setTxnPayLoad={setTxnPayLoad}
            />
          </LargePopupRedirect>
        </div>
      )}
      <div className="w-width-full">
        <div
          className={`bg-emphasis flex flex-col relative rounded-border-radius-md`}
        >
          <div
            className={`bg-primary-nav flex flex-col relative rounded-border-radius-md`}
          >
            <div
              className={`p-padding-md h-height-full bg-primary-card flex flex-col rounded-border-radius-md`}
            >
              {page !== "confirm" &&
              ((txnPayLoad?.bankAccountNo &&
                txnPayLoad?.bankAccountIFSC &&
                type !== "source") ||
                (txnPayLoad?.ownerBankAccountNo &&
                  txnPayLoad?.ownerBankIFSC &&
                  type === "source")) ? (
                <div className="top-top-base right-right-base absolute">
                  <IconTearDropButton
                    handleFunction={handleOpenModal}
                    isDisabled={false}
                    isLoading={false}
                    enabledBgColor={"var(--primary-nav-act)"}
                    icon="https://q2pay.in/new_assets/common/create_flow/edit.svg"
                    hoverOption="white"
                    title={TEXT?.EDIT}
                  />
                </div>
              ) : null}
              <div
                className={`flex flex-col gap-gap-md ${(txnPayLoad?.bankAccountNo && txnPayLoad?.bankAccountIFSC && type !== "source") || (txnPayLoad?.ownerBankAccountNo && txnPayLoad?.ownerBankIFSC && type === "source") ? " mb-margin-xl" : ""}`}
              >
                <div className="flex flex-col gap-gap-xs">
                  <span className="text-font-md text-text-primary font-semibold tracking-letter-space">
                    {TEXT?.DESTINATION_ACCOUNT_CAPITALIZE}
                  </span>
                  <span className="text-font-xs text-text-secondary">
                    {TEXT?.DESTINATION_ACCOUNT_DES_TEXT}
                  </span>
                </div>

                {(!txnPayLoad?.bankAccountNo &&
                  !txnPayLoad?.bankAccountIFSC &&
                  type !== "source") ||
                (!txnPayLoad?.ownerBankAccountNo &&
                  !txnPayLoad?.ownerBankIFSC &&
                  type === "source") ? (
                  <div>
                    <PrimaryWithIconButton
                      btnText={"Select reimbursement account"}
                      enabledBgColor="var(--primary-nav-act)"
                      isDisabled={false}
                      handleFunction={handleOpenModal}
                      iconToBeAlign="left"
                      enabledTextColor={"var(--text-primary)"}
                      icon={ICONS?.SELECT_ACCOUNT}
                      fullWidthRequired={true}
                      hoverOption="white"
                    />
                    {type === "destination" &&
                      flow === "beneficiary" &&
                      !txnPayLoad?.bankAccountNo &&
                      !txnPayLoad?.bankAccountIFSC && (
                        <div className="mt-margin-md">
                          <PrimaryWithIconButton
                            btnText={TEXT?.REGISTER_NEW_ACCOUNT}
                            enabledBgColor="var(--primary-nav-act)"
                            handleFunction={registerNewAccount}
                            isDisabled={!hasBUAccountService}
                            iconToBeAlign="left"
                            enabledTextColor="var(--text-primary)"
                            icon={
                              "https://q2pay.in/new_assets/common/settings/add_button_white.svg"
                            }
                            imgColor="var(--text-primary)"
                            fullWidthRequired
                            hoverOption="white"
                          />
                        </div>
                      )}
                  </div>
                ) : (txnPayLoad?.bankAccountNo &&
                    txnPayLoad?.bankAccountIFSC &&
                    type !== "source") ||
                  (txnPayLoad?.ownerBankAccountNo &&
                    txnPayLoad?.ownerBankIFSC &&
                    type === "source") ? (
                  <>
                    <div className="flex flex-col gap-gap-md">
                      <div className="flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-xs">
                        <span className="text-font-sm text-text-primary font-semibold tracking-letter-space">
                          {formatAccountNumber(txnPayLoad?.bankAccountNo)}
                        </span>
                        <span className="text-font-xs text-text-secondary">
                          {" "}
                          {TEXT?.ACCOUNT_NUMBER}
                        </span>
                      </div>

                      <div className="flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-xs">
                        <div className="text-font-sm text-text-primary font-semibold tracking-letter-space">
                          <div className="flex-1 truncate flex">
                            <TickerAnimation
                              text={accountNameStandardised(
                                txnPayLoad?.bankAccountName,
                              )}
                            />
                          </div>
                        </div>
                        <span className="text-font-xs text-text-secondary">
                          {" "}
                          {TEXT?.ACCOUNT_NAME}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between absolute right-right-base bottom-bottom-base">
                      <div className="bg-text-primary rounded-tl-border-radius-md rounded-br-border-radius-md flex items-center justify-around w-width-full h-full py-padding-sm px-padding-md">
                        <CommonBankLogo
                          ifsc={
                            type === "destination"
                              ? txnPayLoad?.bankAccountIFSC
                              : txnPayLoad?.ownerBankIFSC
                          }
                          variant="big"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="w-width-full flex flex-col gap-gap-md">
              {shouldShowAssociatedAccounts && (
                <div className="py-padding-sm px-padding-sm">
                  <div className="rounded-border-radius-md flex w-width-full px-padding-sm py-padding-sm global-hover-black z-z-index-content relative hover:rounded-border-radius-md">
                    <div
                      className="w-width-full flex items-center text-text-primary cursor-pointer"
                      onClick={handleAssociateOpenModal}
                    >
                      <div className="flex items-start w-width-full">
                        <div className="pt-padding-xs h-icon-md w-icon-md">
                          <img
                            src={ICONS?.INFO_FILLED}
                            className="h-icon-md w-icon-md"
                          />
                        </div>
                        <div className="flex flex-col gap-gap-xs ml-margin-sm">
                          <div className="text-text-primary text-font-md">
                            {TEXT?.ACTIVE_ASSOCIATED_ACCOUNTS}
                          </div>
                          <div className="text-text-secondary text-font-xs">
                            {"Other existing reimbursement accounts found"}
                          </div>
                        </div>
                      </div>

                      <div className="h-height-full pt-padding-xs w-icon-md cursor-pointer flex-shrink-0">
                        <SVGComponent
                          url={
                            "https://q2pay.in/new_assets/common/arrows/arrow_right_white.svg"
                          }
                          color={"var(--text-primary)"}
                          height={"var(--icon-md)"}
                          width={"var(--icon-md)"}
                          alt="Arrow"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DestinationFieldForce;
